'use client';

import { useCallback, useState } from 'react';
import { BriefcaseIcon, CheckIcon, HomeIcon, MapPinIcon } from '@/components/icons';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAddressBook } from '@/hooks/use-address-book';
import { applySavedAddress } from '@/lib/saved-address';
import { useCheckoutStore, selectDeliveryDetails } from '@/stores/checkout-store';
import type { AddressType, SavedAddress } from '@/lib/api/addresses';

const TYPE_ICONS: Record<AddressType, React.ElementType> = {
  home: HomeIcon,
  work: BriefcaseIcon,
  other: MapPinIcon,
};

/**
 * Выбор адреса из книги прямо на шаге «Данные доставки».
 *
 * Нажатие подставляет и адрес, и квартиру с подъездом, этажом и домофоном —
 * это и есть ответ на «заполнил в профиле, потом заполнял то же самое в корзине».
 *
 * Компонент ничего не показывает, если книга пуста или человек не авторизован:
 * лишняя пустая карточка перед оплатой только мешает.
 */
export function SavedAddressPicker() {
  const { addresses, isLoading } = useAddressBook();
  const deliveryDetails = useCheckoutStore(selectDeliveryDetails);
  const [applyingId, setApplyingId] = useState<number | null>(null);

  const handleSelect = useCallback(async (saved: SavedAddress) => {
    setApplyingId(saved.id);
    try {
      const inZone = await applySavedAddress(saved);
      if (!inZone) {
        // Зона могла измениться с момента сохранения. Молчать нельзя: человек
        // нажал на свой адрес и должен понимать, почему ничего не произошло.
        toast.error('По этому адресу доставка сейчас недоступна');
      }
    } catch {
      toast.error('Не удалось подставить адрес');
    } finally {
      setApplyingId(null);
    }
  }, []);

  if (isLoading || addresses.length === 0) return null;

  return (
    <div className="mb-4">
      <p className="mb-2 text-sm font-medium text-[var(--text-secondary)]">
        Мои адреса
      </p>
      <div className="flex flex-wrap gap-2">
        {addresses.map((saved) => {
          const Icon = TYPE_ICONS[saved.type] ?? MapPinIcon;
          const isActive = deliveryDetails.savedAddressId === saved.id;
          const isBusy = applyingId === saved.id;

          return (
            <button
              key={saved.id}
              type="button"
              onClick={() => void handleSelect(saved)}
              disabled={applyingId !== null}
              className={cn(
                'flex items-center gap-2 max-w-full px-3 py-2 rounded-card border text-left transition-colors',
                'disabled:opacity-60',
                isActive
                  ? 'border-[var(--primary)] bg-[var(--primary)]/10'
                  : 'border-[var(--border-default)] bg-[var(--surface-secondary)] hover:border-[var(--primary)]/50'
              )}
            >
              {isBusy ? (
                <span className="h-4 w-4 flex-shrink-0 rounded-pill border-2 border-[var(--primary)] border-t-transparent animate-spin" />
              ) : isActive ? (
                <CheckIcon className="h-4 w-4 flex-shrink-0 text-[var(--primary)]" weight="bold" />
              ) : (
                <Icon className="h-4 w-4 flex-shrink-0 text-[var(--text-tertiary)]" weight="fill" />
              )}
              <span className="min-w-0">
                <span className="block text-xs font-semibold text-[var(--text-primary)] truncate">
                  {saved.label || 'Адрес'}
                </span>
                <span className="block text-[11px] text-[var(--text-tertiary)] truncate">
                  {saved.address}
                  {saved.apartment ? `, кв. ${saved.apartment}` : ''}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
